import { useState, useEffect, useRef } from 'react';
import { appConfig } from '../config';
import './Login.css';

interface LoginProps {
  // Resolves to false when the credentials were rejected, so we can clear the password.
  onLogin: (email: string, password: string, rememberMe: boolean) => Promise<boolean | void>;
  onRegister: () => void;
  error?: string | null;
}

export function Login({ onLogin, onRegister, error }: LoginProps) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(() => {
    // Load saved preference
    return localStorage.getItem('gamedate_remember_me') === 'true';
  });
  const [isLoading, setIsLoading] = useState(false);
  // Synchronous re-entry guard: setIsLoading(true) only reflects on the NEXT render, so a fast
  // mobile double-tap can fire handleSubmit twice before the button disables. This ref blocks the
  // second call in the same tick.
  const submitInFlightRef = useRef(false);

  // Load saved email if remember me was checked
  useEffect(() => {
    if (rememberMe) {
      const savedEmail = localStorage.getItem('gamedate_saved_email');
      if (savedEmail) setEmail(savedEmail);
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) return;
    if (submitInFlightRef.current) return;
    submitInFlightRef.current = true;

    // Save preferences
    localStorage.setItem('gamedate_remember_me', String(rememberMe));
    if (rememberMe) {
      localStorage.setItem('gamedate_saved_email', email);
    } else {
      localStorage.removeItem('gamedate_saved_email');
    }

    setIsLoading(true);
    try {
      const ok = await onLogin(email, password, rememberMe);
      // On rejection (invalid / banned / suspended) clear the password but keep the email,
      // so the user can retry without retyping their address. (US-2 AC-2)
      if (ok === false) {
        setPassword('');
      }
    } finally {
      setIsLoading(false);
      submitInFlightRef.current = false;
    }
  };

  return (
    <div className="login-container">
      <div className="login-card">
        <div className="login-header">
          {/* Mode-aware branding: "GameDate" or "Quest Academy" per active preset */}
          <h1>{appConfig.name}</h1>
          <p>{appConfig.description}</p>
        </div>

        {/* Form with proper attributes for browser password manager */}
        <form
          onSubmit={handleSubmit}
          className="login-form"
          autoComplete="on"
          method="post"
          action="#"
        >
          {error && <div className="login-error">{error}</div>}

          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              id="email"
              name="email"
              type="email"
              autoComplete="username email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
              disabled={isLoading}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <input
              id="password"
              name="password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your password"
              disabled={isLoading}
              required
            />
          </div>

          <div className="remember-me-group">
            <label className="remember-me-label">
              <input
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                className="remember-me-checkbox"
              />
              <span className="remember-me-checkmark"></span>
              <span className="remember-me-text">Remember me</span>
            </label>
          </div>

          <button type="submit" className="login-btn" disabled={isLoading}>
            {isLoading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>

        <div className="login-divider">
          <span>or</span>
        </div>

        <button
          className="register-btn"
          onClick={onRegister}
          disabled={isLoading}
        >
          Create New Account
        </button>
      </div>
    </div>
  );
}

export default Login;
